import { assign, fromCallback, fromPromise, setup } from 'xstate';
import type { AnyActorRef } from 'xstate';
import { firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';

import type { EstimatedPickupTime } from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';
import triplegUtils from '@/services/triplegUtils';

import type { TripArrayFieldContext } from './createTripArrayFieldMachine';

/** Subset of Google Directions results used to populate travel time UI. */
export type DirectionsResultLike = {
  routes?: Array<{
    legs?: Array<{
      duration?: { text?: string };
      distance?: { text?: string };
    }>;
  }>;
};

export type GetDirectionsFn = (
  origin: string,
  destination: string,
) => Promise<DirectionsResultLike | undefined>;

export type DirectionsTravelDisplay = {
  durationText: string;
  distanceText: string;
};

export type TripLegEstimatedPickupInput = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  /** Which trip leg row to read from the pickup/dropoff array machines (default 0). */
  tripIndex?: number;
  /** From `GoogleMapsContext`; used to populate travel time UI for the first leg. */
  getDirections?: GetDirectionsFn;
  /** Provides appointment date/time values (ISO strings) per trip leg. */
  appointmentDateTimeRef?: AnyActorRef;
  /** Returns the IANA pickup-location timezone for this trip leg at call time. */
  getPickupTimeZone?: () => string | undefined;
  /** Returns the form's `*AddressStruct` JSON for a trip leg at call time. */
  getAddressStructs?: GetAddressStructsFn;
};

/** The pickup/dropoff `*AddressStruct` JSON strings stored for one trip leg. */
export type GetAddressStructsFn = (tripIndex: number) => {
  pickup?: string;
  dropoff?: string;
};

export type TripLegEstimatedPickupContext = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  tripIndex: number;
  estimatedPickupTime: EstimatedPickupTime | null;
  /** Human-readable driving time/distance from Google Directions (first leg), when available. */
  directionsTravel: DirectionsTravelDisplay | null;
  getDirections?: GetDirectionsFn;
  appointmentDateTimeRef?: AnyActorRef;
  getPickupTimeZone?: () => string | undefined;
  getAddressStructs?: GetAddressStructsFn;
  /** Set while a request is in flight; used to ignore duplicate address updates. */
  pendingRequestKey: string | null;
  /** Key for the last successful `getEstimatedTripTime` response. */
  lastFetchedKey: string | null;
  fetchError: unknown | null;
};

function readTripArrayValues(ref: AnyActorRef): unknown[] {
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

type AppointmentFields =
  | { appointmentDate: string; appointmentTime: string; timeZone: string }
  | { appointmentDateTime: string };

/**
 * Builds the calculate-pickup-time appointment payload from a stored ISO string.
 * Uses Option B (date + time + IANA timezone) when pickup timezone is available,
 * falls back to Option A (ISO string) otherwise.
 *
 * `setZone: true` keeps the wall-clock from the ISO offset/zone. Without it,
 * Luxon converts into Settings.defaultZone and Option B can send the wrong HH:mm
 * (e.g. 17:00-07:00 → 20:00 in America/New_York).
 */
export function buildAppointmentFieldsFromIso(
  iso: string,
  timeZone?: string,
): AppointmentFields | undefined {
  if (!iso) return undefined;
  const dt = DateTime.fromISO(iso, { setZone: true });
  if (!dt.isValid) return undefined;
  if (timeZone) {
    const appointmentDate = dt.toISODate();
    if (!appointmentDate) return { appointmentDateTime: iso };
    return {
      appointmentDate,
      appointmentTime: dt.toFormat('HH:mm'),
      timeZone,
    };
  }
  return { appointmentDateTime: iso };
}

/**
 * Reads the ISO string stored by TripArrayFieldDateTimeInput and builds the
 * appointment payload. Returns undefined when no appointment time is set.
 */
function getAppointmentFields(
  ref: AnyActorRef | undefined,
  tripIndex: number,
  getPickupTimeZone?: () => string | undefined,
): AppointmentFields | undefined {
  if (!ref) return undefined;
  const val = readTripArrayValues(ref)[tripIndex];
  const iso = typeof val === 'string' ? val : '';
  return buildAppointmentFieldsFromIso(iso, getPickupTimeZone?.());
}

/**
 * The pickup/dropoff strings sent to /calculate-pickup-time for this leg. Built
 * from the leg's `*AddressStruct` (the fields /score/trip-leg scores) and only
 * falling back to the autocomplete value when the struct is empty. Also drives
 * the empty-address guard and the fetch dedupe key, so all three agree.
 */
function getPairAtIndex(
  context: Pick<
    TripLegEstimatedPickupContext,
    'pickupAddressRef' | 'dropoffAddressRef' | 'tripIndex' | 'getAddressStructs'
  >,
): {
  pickupLine: string;
  dropoffLine: string;
} {
  const { tripIndex } = context;
  const pickups = readTripArrayValues(context.pickupAddressRef);
  const dropoffs = readTripArrayValues(context.dropoffAddressRef);
  // Same fallback to leg 0 as the address values, so value and struct match.
  const pickupIndex = pickups[tripIndex] !== undefined ? tripIndex : 0;
  const dropoffIndex = dropoffs[tripIndex] !== undefined ? tripIndex : 0;
  const pickupStruct = context.getAddressStructs?.(pickupIndex)?.pickup;
  const dropoffStruct = context.getAddressStructs?.(dropoffIndex)?.dropoff;
  return {
    pickupLine: triplegUtils.getPickupTimeAddressLine(
      pickupStruct,
      triplegUtils.getAddressOptionLine(pickups[pickupIndex]),
    ),
    dropoffLine: triplegUtils.getPickupTimeAddressLine(
      dropoffStruct,
      triplegUtils.getAddressOptionLine(dropoffs[dropoffIndex]),
    ),
  };
}

function buildFetchKey(
  pickupLine: string,
  dropoffLine: string,
  appt: AppointmentFields | undefined,
): string {
  const apptKey = appt
    ? 'appointmentDateTime' in appt
      ? appt.appointmentDateTime
      : `${appt.appointmentDate}T${appt.appointmentTime}@${appt.timeZone}`
    : '';
  return `${pickupLine}\0${dropoffLine}\0${apptKey}`;
}

const addressPairSubscription = fromCallback(
  ({
    sendBack,
    input,
  }: {
    sendBack: (event: { type: 'ADDRESSES_UPDATED' }) => void;
    input: Pick<
      TripLegEstimatedPickupContext,
      'pickupAddressRef' | 'dropoffAddressRef' | 'appointmentDateTimeRef'
    >;
  }) => {
    const emit = () => {
      sendBack({ type: 'ADDRESSES_UPDATED' });
    };
    const sub1 = input.pickupAddressRef.subscribe(emit);
    const sub2 = input.dropoffAddressRef.subscribe(emit);
    const sub3 = input.appointmentDateTimeRef?.subscribe(emit);
    emit();
    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
      sub3?.unsubscribe();
    };
  },
);

export const tripLegEstimatedPickupMachine = setup({
  types: {
    context: {} as TripLegEstimatedPickupContext,
    events: {} as { type: 'ADDRESSES_UPDATED' } | { type: 'RESET' },
    input: {} as TripLegEstimatedPickupInput,
  },
  actors: {
    addressPairSubscription,
    fetchTripLegEstimates: fromPromise<
      {
        estimatedPickupTime: EstimatedPickupTime;
        directionsTravel: DirectionsTravelDisplay | null;
      },
      {
        pickUpAddress: string;
        dropOffAddress: string;
        getDirections?: GetDirectionsFn;
        appointment?: AppointmentFields | undefined;
      }
    >(async ({ input }) => {
      const estimatedPickupTime = await firstValueFrom(
        reservationService.getEstimatedTripTime(
          input.pickUpAddress,
          input.dropOffAddress,
          input.appointment,
        ),
      );

      const { pickUpAddress, dropOffAddress, getDirections } = input;
      const pickupStr = String(pickUpAddress ?? '').trim();
      const dropoffStr = String(dropOffAddress ?? '').trim();

      let directionsTravel: DirectionsTravelDisplay | null = null;
      if (getDirections && pickupStr && dropoffStr) {
        try {
          const dir = await getDirections(pickupStr, dropoffStr);
          const leg = dir?.routes?.[0]?.legs?.[0];
          const durationText = leg?.duration?.text;
          const distanceText = leg?.distance?.text;
          if (durationText && distanceText) {
            directionsTravel = { durationText, distanceText };
          }
        } catch {
          // Directions are optional; server estimate still applies.
        }
      }

      return { estimatedPickupTime, directionsTravel };
    }),
  },
}).createMachine({
  id: 'tripLegEstimatedPickup',
  context: ({ input }) => ({
    pickupAddressRef: input.pickupAddressRef,
    dropoffAddressRef: input.dropoffAddressRef,
    tripIndex: input.tripIndex ?? 0,
    estimatedPickupTime: null,
    directionsTravel: null,
    getDirections: input.getDirections,
    appointmentDateTimeRef: input.appointmentDateTimeRef,
    getPickupTimeZone: input.getPickupTimeZone,
    getAddressStructs: input.getAddressStructs,
    pendingRequestKey: null,
    lastFetchedKey: null,
    fetchError: null,
  }),
  initial: 'active',
  states: {
    active: {
      invoke: {
        id: 'addressPairSubscription',
        src: 'addressPairSubscription',
        input: ({ context }) => ({
          pickupAddressRef: context.pickupAddressRef,
          dropoffAddressRef: context.dropoffAddressRef,
          appointmentDateTimeRef: context.appointmentDateTimeRef,
        }),
      },
      initial: 'ready',
      states: {
        ready: {
          on: {
            ADDRESSES_UPDATED: [
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(context);
                  return !pickupLine.trim() || !dropoffLine.trim();
                },
                actions: assign({
                  estimatedPickupTime: () => null,
                  directionsTravel: () => null,
                  fetchError: () => null,
                  lastFetchedKey: () => null,
                  pendingRequestKey: () => null,
                }),
              },
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(context);
                  if (!pickupLine.trim() || !dropoffLine.trim()) return false;
                  const appt = getAppointmentFields(
                    context.appointmentDateTimeRef,
                    context.tripIndex,
                    context.getPickupTimeZone,
                  );
                  const key = buildFetchKey(pickupLine, dropoffLine, appt);
                  return key !== context.lastFetchedKey;
                },
                target: 'fetching',
              },
            ],
            RESET: {
              actions: assign({
                estimatedPickupTime: () => null,
                directionsTravel: () => null,
                fetchError: () => null,
                lastFetchedKey: () => null,
                pendingRequestKey: () => null,
              }),
            },
          },
        },
        fetching: {
          entry: assign({
            pendingRequestKey: ({ context }) => {
              const { pickupLine, dropoffLine } = getPairAtIndex(context);
              const appt = getAppointmentFields(
                context.appointmentDateTimeRef,
                context.tripIndex,
                context.getPickupTimeZone,
              );
              return buildFetchKey(pickupLine, dropoffLine, appt);
            },
            fetchError: () => null,
          }),
          invoke: {
            id: 'fetchTripLegEstimates',
            src: 'fetchTripLegEstimates',
            input: ({ context }) => {
              const { pickupLine, dropoffLine } = getPairAtIndex(context);
              const appt = getAppointmentFields(
                context.appointmentDateTimeRef,
                context.tripIndex,
                context.getPickupTimeZone,
              );
              return {
                pickUpAddress: pickupLine,
                dropOffAddress: dropoffLine,
                getDirections: context.getDirections,
                appointment: appt,
              };
            },
            onDone: {
              target: 'ready',
              actions: assign(({ context, event }) => {
                const output = event.output as {
                  estimatedPickupTime: EstimatedPickupTime;
                  directionsTravel: DirectionsTravelDisplay | null;
                };
                const { pickupLine, dropoffLine } = getPairAtIndex(context);
                const appt = getAppointmentFields(
                  context.appointmentDateTimeRef,
                  context.tripIndex,
                  context.getPickupTimeZone,
                );
                return {
                  estimatedPickupTime: output.estimatedPickupTime,
                  directionsTravel: output.directionsTravel,
                  fetchError: null,
                  lastFetchedKey: buildFetchKey(pickupLine, dropoffLine, appt),
                  pendingRequestKey: null,
                };
              }),
            },
            onError: {
              target: 'ready',
              actions: assign({
                estimatedPickupTime: () => null,
                directionsTravel: () => null,
                fetchError: ({ event }) => event.error,
                pendingRequestKey: () => null,
              }),
            },
          },
          on: {
            ADDRESSES_UPDATED: [
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(context);
                  return !pickupLine.trim() || !dropoffLine.trim();
                },
                target: 'ready',
                actions: assign({
                  estimatedPickupTime: () => null,
                  directionsTravel: () => null,
                  fetchError: () => null,
                  lastFetchedKey: () => null,
                  pendingRequestKey: () => null,
                }),
              },
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(context);
                  if (!pickupLine.trim() || !dropoffLine.trim()) return false;
                  const appt = getAppointmentFields(
                    context.appointmentDateTimeRef,
                    context.tripIndex,
                    context.getPickupTimeZone,
                  );
                  const key = buildFetchKey(pickupLine, dropoffLine, appt);
                  return (
                    context.pendingRequestKey != null &&
                    key !== context.pendingRequestKey
                  );
                },
                target: 'fetching',
                reenter: true,
              },
            ],
            RESET: {
              target: 'ready',
              actions: assign({
                estimatedPickupTime: () => null,
                directionsTravel: () => null,
                fetchError: () => null,
                lastFetchedKey: () => null,
                pendingRequestKey: () => null,
              }),
            },
          },
        },
      },
      on: {
        RESET: {
          actions: assign({
            estimatedPickupTime: () => null,
            directionsTravel: () => null,
            fetchError: () => null,
            lastFetchedKey: () => null,
            pendingRequestKey: () => null,
          }),
        },
      },
    },
  },
});
